import ShareIcon from '@mui/icons-material/Share';
import clsx from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import GaugeChart from 'react-gauge-chart';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  type Oth360LeaderProfile,
  getOth360LeaderProfile,
} from 'src/api/oth360LeaderProfile';
import { MaterialCircularProgress } from 'src/components/material/CircularProgress';
import { Oth360Banner } from 'src/components/oth/Oth360Banner';
import { PathRoutes } from 'src/const/Routes/PathRoutes';
import { getPreferredLanguage } from 'src/i18n';
import { loadOth360InviteContext } from './oth360InviteStorage';
import s from './Oth360ResultsPage.module.scss';

type TabId = 'status' | 'fortsett' | 'slutt' | 'start' | 'oppsummering';

const GAP_DIMENSIONS: Array<{
  key:
    | 'strategy_direction'
    | 'organizing_roles'
    | 'culture_values'
    | 'execution_power'
    | 'psychological_safety'
    | 'development_feedback';
  labelKey: string;
}> = [
  {
    key: 'strategy_direction',
    labelKey: 'oth360Results.status.dimensions.strategy_direction',
  },
  {
    key: 'organizing_roles',
    labelKey: 'oth360Results.status.dimensions.organizing_roles',
  },
  {
    key: 'culture_values',
    labelKey: 'oth360Results.status.dimensions.culture_values',
  },
  {
    key: 'execution_power',
    labelKey: 'oth360Results.status.dimensions.execution_power',
  },
  {
    key: 'psychological_safety',
    labelKey: 'oth360Results.status.dimensions.psychological_safety',
  },
  {
    key: 'development_feedback',
    labelKey: 'oth360Results.status.dimensions.development_feedback',
  },
];

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function toScalePercent(value1to6: number) {
  // Map 1..6 to 0..100
  const v = clamp(value1to6, 1, 6);
  return ((v - 1) / 5) * 100;
}

function toGaugePercent(value1to6: number) {
  // Map 1..6 to 0..1 (react-gauge-chart)
  const v = clamp(value1to6, 1, 6);
  return (v - 1) / 5;
}

function toNumberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function toApiLang(language: 'no' | 'da' | 'en'): 'nb' | 'da' | 'en' {
  if (language === 'no') {
    return 'nb';
  }
  return language;
}

function toAcceptLanguage(language: 'nb' | 'da' | 'en') {
  if (language === 'nb') {
    return 'nb-NO,nb;q=0.9,no;q=0.8';
  }
  if (language === 'da') {
    return 'da-DK,da;q=0.9';
  }
  return 'en-GB,en;q=0.9';
}

function formatScore(value: unknown) {
  const n = toNumberOrNull(value);
  return n === null ? '—' : n.toFixed(1);
}

type Oth360AiSummaryResponse = {
  result: {
    version: string;
    privacy: {
      minAnon: number;
      respondentsN: number;
      quotesEnabled: boolean;
      notes?: string;
    };
    executiveSummary: {
      overall: string;
      topStrengths: string[];
      topGrowthAreas: string[];
    };
    themes: Array<{
      id: string;
      title: string;
      signal: 'fortsett' | 'slutt' | 'start' | string;
      mentions: number;
      summary: string;
      representativeQuotes?: string[];
      suggestedActions?: string[];
    }>;
    gapInsights?: Array<{
      dimensionId: string;
      dimensionTitle: string;
      self: number;
      othersMean: number;
      gap: number;
      interpretation: string;
      supportingThemes?: string[];
    }>;
    actionPlan?: {
      next14Days?: string[];
      next60Days?: string[];
      questionsToAskTeam?: string[];
    };
    risksAndWatchouts?: Array<{
      title: string;
      whyItMatters: string;
      whatToDo: string;
    }>;
    limitations?: string[];
  };
};

async function shareOrCopy(url: string, title: string) {
  try {
    if (navigator.share) {
      await navigator.share({ title, url });
      return { ok: true, mode: 'share' as const };
    }
    await navigator.clipboard.writeText(url);
    return { ok: true, mode: 'copy' as const };
  } catch {
    return { ok: false, mode: 'error' as const };
  }
}

export const Oth360ResultsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const ctx = useMemo(() => loadOth360InviteContext(), []);
  const [tab, setTab] = useState<TabId>('status');
  const [isLoading, setIsLoading] = useState(true);
  const [notSubmittedYet, setNotSubmittedYet] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [profile, setProfile] = useState<Oth360LeaderProfile | null>(null);
  const [aiSummary, setAiSummary] = useState<
    Oth360AiSummaryResponse['result'] | null
  >(null);
  const [aiIsThinking, setAiIsThinking] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const isLocalDevHost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1');

  const onShareEmployeeLink = async () => {
    const link = ctx?.employeeLink;
    if (!link) {
      toast.error(t('oth360Results.share.employeeLinkMissing'));
      return;
    }
    const r = await shareOrCopy(link, t('oth360Results.share.title'));
    if (!r.ok) {
      toast.error(t('oth360Results.share.error'));
    } else {
      toast.success(
        r.mode === 'copy'
          ? t('oth360Results.share.copied')
          : t('oth360Results.share.shared')
      );
    }
  };

  useEffect(() => {
    if (ctx && !ctx.hasSubmitted) {
      navigate(PathRoutes.Oth360FormRoute, { replace: true });
    }
  }, [ctx, navigate]);

  async function fetchAiSummary(input?: { mock?: boolean; minAnon?: number }) {
    // UX: Do not generate automatically. Only call when user presses the CTA.
    setAiIsThinking(true);
    setAiError(null);

    try {
      const uiLanguage = getPreferredLanguage('no');
      const apiLanguage = toApiLang(uiLanguage);
      const url = new URL('/api/oth/360/ai-summary', window.location.origin);
      if (input?.mock) {
        url.searchParams.set('mock', 'true');
      } else {
        url.searchParams.set('mode', 'self');
      }
      url.searchParams.set('lang', apiLanguage);
      if (typeof input?.minAnon === 'number') {
        url.searchParams.set('minAnon', String(input.minAnon));
      }

      const res = await fetch(url.toString(), {
        method: 'GET',
        // Normal mode uses the 360 session cookie (oth360_self_session).
        // Mock mode should not require any cookies/tokens.
        credentials: input?.mock ? 'omit' : 'include',
        headers: {
          Accept: 'application/json',
          'Accept-Language': toAcceptLanguage(apiLanguage),
        },
        cache: 'no-store',
      });

      if (!res.ok) {
        // Non-blocking error: show message and allow retry.
        throw new Error(
          res.status === 403
            ? t('oth360Results.summarySection.forbidden')
            : t('oth360Results.summarySection.fetchError')
        );
      }

      const data = (await res.json()) as Oth360AiSummaryResponse;
      setAiSummary(data.result);
    } catch (err) {
      setAiError(
        err instanceof Error && err.message
          ? err.message
          : t('oth360Results.summarySection.fetchError')
      );
    } finally {
      setAiIsThinking(false);
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setLoadError(null);
      setNotSubmittedYet(false);

      const res = await getOth360LeaderProfile({ mode: 'SELF' });
      if (cancelled) {
        return;
      }

      if (res.ok) {
        setProfile(res.data);
        setIsLoading(false);
        return;
      }

      if (res.status === 403 && res.error?.code === 'SELF_NOT_SUBMITTED') {
        setNotSubmittedYet(true);
        setIsLoading(false);
        return;
      }

      setLoadError(t('oth360Results.loadError.defaultMessage'));
      setIsLoading(false);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [t]);

  // Note: we intentionally do NOT fetch AI summary on tab-open.
  // The user must press the CTA to trigger generation (or retrieval) the first time.

  // This page is only meant to be reachable after the leader has submitted.
  // If we don't have context (or not submitted), send them back to the form entry.
  if (!ctx) {
    return (
      <div className={s.page}>
        <div className={s.content}>
          <p>{t('oth360Results.missingContext.message')}</p>
          <div className={s.actions}></div>
        </div>
      </div>
    );
  }

  if (!ctx.hasSubmitted) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={s.page}>
        <div className={s.content}>
          <MaterialCircularProgress />
        </div>
      </div>
    );
  }

  if (notSubmittedYet) {
    return (
      <div className={s.page}>
        <div className={s.content}>
          <div className={s.placeholderCard}>
            <p className={s.placeholderTitle}>
              {t('oth360Results.notSubmittedYet.title')}
            </p>
            <p className={s.placeholderText}>
              {t('oth360Results.notSubmittedYet.message')}
            </p>
          </div>
          <div className={s.actions}>
            <button
              type="button"
              className={s.primaryButton}
              onClick={() => navigate(PathRoutes.Oth360FormRoute)}
            >
              {t('oth360Results.notSubmittedYet.goToForm')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loadError || !profile) {
    return (
      <div className={s.page}>
        <div className={s.content}>
          <div className={s.placeholderCard}>
            <p className={s.placeholderTitle}>
              {t('oth360Results.loadError.title')}
            </p>
            <p className={s.placeholderText}>
              {loadError || t('oth360Results.loadError.defaultMessage')}
            </p>
          </div>
          <div className={s.actions}>
            <button
              type="button"
              className={s.primaryButton}
              onClick={() => window.location.reload()}
            >
              {t('oth360Results.loadError.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const trustAvg = toNumberOrNull(profile.trust.employeesAvg) ?? 0;
  const trustCount = toNumberOrNull(profile.trust.employeesCount) ?? 0;
  const trustPercent = toGaugePercent(trustAvg || 1);

  return (
    <div className={s.page}>
      <Oth360Banner
        title={t('oth360Results.title')}
        actions={
          <button
            type="button"
            className={clsx(s.heroButton, s.shareButton)}
            onClick={onShareEmployeeLink}
            disabled={!ctx?.employeeLink}
          >
            {t('oth360Results.share.button')}{' '}
            <ShareIcon className={s.shareIcon} fontSize="small" />
          </button>
        }
      />

      <nav className={s.tabs} aria-label={t('oth360Results.tabs.ariaLabel')}>
        <div className={s.mobileSelectWrap}>
          <select
            id="resultTabSelect"
            className={s.mobileSelect}
            value={tab}
            onChange={(e) => setTab(e.target.value as typeof tab)}
          >
            <option value="status">{t('oth360Results.tabs.status')}</option>
            <option value="fortsett">
              {t('oth360Results.tabs.continueWith')}
            </option>
            <option value="slutt">{t('oth360Results.tabs.stopWith')}</option>
            <option value="start">{t('oth360Results.tabs.startWith')}</option>
            <option value="oppsummering">
              {t('oth360Results.tabs.summary')}
            </option>
          </select>
        </div>

        <div className={s.tabsInner}>
          <button
            type="button"
            className={clsx(s.tab, tab === 'status' && s.tabActive)}
            onClick={() => setTab('status')}
          >
            {t('oth360Results.tabs.status')}
          </button>
          <button
            type="button"
            className={clsx(s.tab, tab === 'fortsett' && s.tabActive)}
            onClick={() => setTab('fortsett')}
          >
            {t('oth360Results.tabs.continueWith')}
          </button>
          <button
            type="button"
            className={clsx(s.tab, tab === 'slutt' && s.tabActive)}
            onClick={() => setTab('slutt')}
          >
            {t('oth360Results.tabs.stopWith')}
          </button>
          <button
            type="button"
            className={clsx(s.tab, tab === 'start' && s.tabActive)}
            onClick={() => setTab('start')}
          >
            {t('oth360Results.tabs.startWith')}
          </button>
          <button
            type="button"
            className={clsx(s.tab, tab === 'oppsummering' && s.tabActive)}
            onClick={() => setTab('oppsummering')}
          >
            {t('oth360Results.tabs.summary')}
          </button>
        </div>
      </nav>

      <main className={s.content}>
        {tab === 'status' && (
          <>
            <div className={s.statusGrid}>
              <section
                className={s.statusCol}
                aria-label={t('oth360Results.status.trustAria')}
              >
                <h2 className={s.sectionTitle}>
                  {t('oth360Results.status.trustTitle')}
                </h2>
                <div className={s.trustCard}>
                  <div
                    className={s.gaugeWrap}
                    aria-label={t('oth360Results.status.trustGaugeAria')}
                  >
                    <GaugeChart
                      id="oth360-trust-gauge"
                      nrOfLevels={6}
                      arcPadding={0.02}
                      cornerRadius={0}
                      arcWidth={0.28}
                      percent={trustPercent}
                      colors={[
                        '#ef4444',
                        '#f97316',
                        '#f59e0b',
                        '#a3e635',
                        '#84cc16',
                        '#22c55e',
                      ]}
                      needleColor="#111"
                      needleBaseColor="#111"
                      animate={false}
                      hideText
                    />
                    <div className={s.gaugeCenterOverlay}>
                      <div className={s.gaugeValue}>
                        {formatScore(trustAvg)}
                      </div>
                      <div className={s.gaugeMeta}>
                        {t('oth360Results.status.employeesCount', {
                          count: trustCount,
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section
                className={s.statusCol}
                aria-label={t('oth360Results.status.gapAria')}
              >
                <h2 className={clsx(s.sectionTitle, s.sectionTitleInset)}>
                  {t('oth360Results.status.gapTitle')}
                </h2>
                <div className={s.gapCard}>
                  {GAP_DIMENSIONS.map((d) => {
                    const selfV =
                      toNumberOrNull(profile.gapAnalysis.self[d.key]) ?? 0;
                    const empV =
                      toNumberOrNull(profile.gapAnalysis.employeesAvg[d.key]) ??
                      0;
                    const selfPct = toScalePercent(selfV);
                    const empPct = toScalePercent(empV);
                    const leftPct = Math.min(selfPct, empPct);
                    const widthPct = Math.abs(selfPct - empPct);
                    return (
                      <div key={d.key} className={s.gapRow}>
                        <div className={s.gapLabel}>{t(d.labelKey)}</div>
                        <div className={s.gapTrack} aria-label={t(d.labelKey)}>
                          <span
                            className={s.gapConnector}
                            style={{
                              left: `${leftPct}%`,
                              width: `${widthPct}%`,
                            }}
                            aria-hidden="true"
                          />
                          <span
                            className={clsx(s.gapDot, s.gapDotSelf)}
                            style={{ left: `${selfPct}%` }}
                            title={t('oth360Results.status.scoreMe', {
                              value: selfV,
                            })}
                          />
                          <span
                            className={clsx(s.gapDot, s.gapDotEmployees)}
                            style={{ left: `${empPct}%` }}
                            title={t('oth360Results.status.scoreEmployees', {
                              value: formatScore(empV),
                            })}
                          />
                        </div>
                      </div>
                    );
                  })}
                  <div className={s.gapAxis}>
                    <div className={s.gapAxisTicks} aria-hidden="true">
                      {Array.from({ length: 6 }, (_, i) => i + 1).map((n) => (
                        <div key={n} className={s.gapAxisTick}>
                          {n}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className={s.legend}>
                    <div className={s.legendItem}>
                      <span className={clsx(s.legendDot, s.legendDotSelf)} />
                      <span>{t('oth360Results.status.me')}</span>
                    </div>
                    <div className={s.legendItem}>
                      <span
                        className={clsx(s.legendDot, s.legendDotEmployees)}
                      />
                      <span>{t('oth360Results.status.employees')}</span>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </>
        )}

        {tab === 'fortsett' && (
          <>
            <div className={s.textList}>
              {profile.text.continueWith.length === 0 ? (
                <div className={s.placeholderCard}>
                  <p className={s.placeholderText}>
                    {t('oth360Results.textLists.empty')}
                  </p>
                </div>
              ) : (
                profile.text.continueWith.map((textItem, i) => (
                  <div
                    key={`${i}-${textItem.slice(0, 16)}`}
                    className={s.textListItem}
                  >
                    <p className={s.textListTitle}>
                      {t('oth360Results.textLists.employee', { index: i + 1 })}
                    </p>
                    <p className={s.textListText}>{textItem}</p>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {tab === 'slutt' && (
          <>
            <div className={s.textList}>
              {profile.text.stopWith.length === 0 ? (
                <div className={s.placeholderCard}>
                  <p className={s.placeholderText}>
                    {t('oth360Results.textLists.empty')}
                  </p>
                </div>
              ) : (
                profile.text.stopWith.map((textItem, i) => (
                  <div
                    key={`${i}-${textItem.slice(0, 16)}`}
                    className={s.textListItem}
                  >
                    <p className={s.textListTitle}>
                      {t('oth360Results.textLists.employee', { index: i + 1 })}
                    </p>
                    <p className={s.textListText}>{textItem}</p>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {tab === 'start' && (
          <>
            <div className={s.textList}>
              {profile.text.startWith.length === 0 ? (
                <div className={s.placeholderCard}>
                  <p className={s.placeholderText}>
                    {t('oth360Results.textLists.empty')}
                  </p>
                </div>
              ) : (
                profile.text.startWith.map((textItem, i) => (
                  <div
                    key={`${i}-${textItem.slice(0, 16)}`}
                    className={s.textListItem}
                  >
                    <p className={s.textListTitle}>
                      {t('oth360Results.textLists.employee', { index: i + 1 })}
                    </p>
                    <p className={s.textListText}>{textItem}</p>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {tab === 'oppsummering' && (
          <>
            {(() => {
              const isFirstTime = !aiSummary;

              if (isFirstTime) {
                return (
                  <div className={s.aiEmptyState}>
                    <p className={s.aiTitle}>
                      {t('oth360Results.summarySection.initialTitle')}
                    </p>
                    <p className={s.aiDescription}>
                      {t('oth360Results.summarySection.initialDescription')}
                    </p>

                    <button
                      type="button"
                      className={s.primaryButton}
                      onClick={() => void fetchAiSummary()}
                      disabled={aiIsThinking}
                    >
                      {aiIsThinking
                        ? t('oth360Results.summarySection.thinking')
                        : t('oth360Results.summarySection.generateButton')}
                    </button>

                    {isLocalDevHost && (
                      <div className={s.aiActionsRow}>
                        <button
                          type="button"
                          className={s.aiSecondaryButton}
                          onClick={() => void fetchAiSummary({ mock: true })}
                          disabled={aiIsThinking}
                          title={t('oth360Results.summarySection.mockTitle')}
                        >
                          {t('oth360Results.summarySection.mockLocal')}
                        </button>
                      </div>
                    )}

                    <p className={s.aiHelperText}>
                      {t('oth360Results.summarySection.privacyHint')}
                    </p>

                    {aiIsThinking && (
                      <div className={s.aiInlineLoadingRow}>
                        <span className={s.aiMetaText}>
                          {t('oth360Results.summarySection.thinking')}
                        </span>
                      </div>
                    )}

                    {aiError && <p className={s.aiErrorText}>{aiError}</p>}
                  </div>
                );
              }

              return (
                <div className={s.aiSummaryCard}>
                  <div className={s.aiMetaRow}>
                    <p className={s.aiTitle}>
                      {t('oth360Results.summarySection.title')}
                    </p>
                  </div>

                  {aiIsThinking && (
                    <div className={s.aiInlineLoadingRow}>
                      <span className={s.aiMetaText}>
                        {t('oth360Results.summarySection.thinking')}
                      </span>
                    </div>
                  )}

                  {aiSummary.privacy.notes && (
                    <p className={s.aiHelperText}>{aiSummary.privacy.notes}</p>
                  )}

                  <div className={s.aiSummaryText}>
                    <p className={s.aiParagraph}>
                      {aiSummary.executiveSummary.overall}
                    </p>

                    <div className={s.aiSection}>
                      <p className={s.aiSectionTitle}>
                        {t('oth360Results.summarySection.strengths')}
                      </p>
                      <ul className={s.aiList}>
                        {aiSummary.executiveSummary.topStrengths.map((x, i) => (
                          <li key={`${i}-${x}`} className={s.aiListItem}>
                            {x}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className={s.aiSection}>
                      <p className={s.aiSectionTitle}>
                        {t('oth360Results.summarySection.growthAreas')}
                      </p>
                      <ul className={s.aiList}>
                        {aiSummary.executiveSummary.topGrowthAreas.map(
                          (x, i) => (
                            <li key={`${i}-${x}`} className={s.aiListItem}>
                              {x}
                            </li>
                          )
                        )}
                      </ul>
                    </div>

                    <div className={s.aiSection}>
                      <p className={s.aiSectionTitle}>
                        {t('oth360Results.summarySection.themes')}
                      </p>
                      <div className={s.aiThemes}>
                        {aiSummary.themes.map((themeItem) => (
                          <div key={themeItem.id} className={s.aiThemeCard}>
                            <div className={s.aiThemeHeader}>
                              <p className={s.aiThemeTitle}>
                                {themeItem.title}
                              </p>
                            </div>
                            <p className={s.aiParagraph}>{themeItem.summary}</p>

                            {aiSummary.privacy.quotesEnabled &&
                              themeItem.representativeQuotes &&
                              themeItem.representativeQuotes.length > 0 && (
                                <div className={s.aiSubSection}>
                                  <p className={s.aiSubTitle}>
                                    {t('oth360Results.summarySection.quotes')}
                                  </p>
                                  <ul className={s.aiQuoteList}>
                                    {themeItem.representativeQuotes.map(
                                      (q, i) => (
                                        <li
                                          key={`${themeItem.id}-q-${i}`}
                                          className={s.aiQuoteItem}
                                        >
                                          “{q}”
                                        </li>
                                      )
                                    )}
                                  </ul>
                                </div>
                              )}

                            {themeItem.suggestedActions &&
                              themeItem.suggestedActions.length > 0 && (
                                <div className={s.aiSubSection}>
                                  <p className={s.aiSubTitle}>
                                    {t(
                                      'oth360Results.summarySection.suggestedActions'
                                    )}
                                  </p>
                                  <ul className={s.aiList}>
                                    {themeItem.suggestedActions.map((a, i) => (
                                      <li
                                        key={`${themeItem.id}-a-${i}`}
                                        className={s.aiListItem}
                                      >
                                        {a}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {aiSummary.gapInsights &&
                      aiSummary.gapInsights.length > 0 && (
                        <div className={s.aiSection}>
                          <p className={s.aiSectionTitle}>
                            {t('oth360Results.summarySection.gapInsights')}
                          </p>
                          <div className={s.aiThemes}>
                            {aiSummary.gapInsights.map((g) => (
                              <div
                                key={g.dimensionId}
                                className={s.aiThemeCard}
                              >
                                <div className={s.aiThemeHeader}>
                                  <p className={s.aiThemeTitle}>
                                    {g.dimensionTitle}
                                  </p>
                                  <p className={s.aiMetaText}>
                                    {t(
                                      'oth360Results.summarySection.gapLabel',
                                      {
                                        gap: g.gap,
                                      }
                                    )}
                                  </p>
                                </div>
                                <p className={s.aiParagraph}>
                                  {g.interpretation}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                    {aiSummary.actionPlan && (
                      <div className={s.aiSection}>
                        <p className={s.aiSectionTitle}>
                          {t('oth360Results.summarySection.nextSteps')}
                        </p>
                        {aiSummary.actionPlan.next14Days &&
                          aiSummary.actionPlan.next14Days.length > 0 && (
                            <div className={s.aiActionPlanBlock}>
                              <p className={s.aiSubTitle}>
                                {t('oth360Results.summarySection.next14Days')}
                              </p>
                              <ul className={s.aiList}>
                                {aiSummary.actionPlan.next14Days.map((x, i) => (
                                  <li
                                    key={`14-${i}-${x}`}
                                    className={s.aiListItem}
                                  >
                                    {x}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        {aiSummary.actionPlan.next60Days &&
                          aiSummary.actionPlan.next60Days.length > 0 && (
                            <div className={s.aiActionPlanBlock}>
                              <p className={s.aiSubTitle}>
                                {t('oth360Results.summarySection.next60Days')}
                              </p>
                              <ul className={s.aiList}>
                                {aiSummary.actionPlan.next60Days.map((x, i) => (
                                  <li
                                    key={`60-${i}-${x}`}
                                    className={s.aiListItem}
                                  >
                                    {x}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        {aiSummary.actionPlan.questionsToAskTeam &&
                          aiSummary.actionPlan.questionsToAskTeam.length >
                            0 && (
                            <div className={s.aiActionPlanBlock}>
                              <p className={s.aiSubTitle}>
                                {t(
                                  'oth360Results.summarySection.questionsForTeam'
                                )}
                              </p>
                              <ul className={s.aiList}>
                                {aiSummary.actionPlan.questionsToAskTeam.map(
                                  (x, i) => (
                                    <li
                                      key={`q-${i}-${x}`}
                                      className={s.aiListItem}
                                    >
                                      {x}
                                    </li>
                                  )
                                )}
                              </ul>
                            </div>
                          )}
                      </div>
                    )}

                    {aiSummary.risksAndWatchouts &&
                      aiSummary.risksAndWatchouts.length > 0 && (
                        <div className={s.aiSection}>
                          <p className={s.aiSectionTitle}>
                            {t('oth360Results.summarySection.watchouts')}
                          </p>
                          <div className={s.aiThemes}>
                            {aiSummary.risksAndWatchouts.map((r, i) => (
                              <div
                                key={`${r.title}-${i}`}
                                className={s.aiThemeCard}
                              >
                                <p className={s.aiThemeTitle}>{r.title}</p>
                                <p className={s.aiParagraph}>
                                  {r.whyItMatters}
                                </p>
                                <p className={s.aiParagraph}>{r.whatToDo}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>

                  <div className={s.aiActionsRow}>
                    <button
                      type="button"
                      className={s.aiSecondaryButton}
                      onClick={() => void fetchAiSummary()}
                      disabled={aiIsThinking}
                    >
                      {t('oth360Results.summarySection.refresh')}
                    </button>

                    {isLocalDevHost && (
                      <button
                        type="button"
                        className={s.aiSecondaryButton}
                        onClick={() => void fetchAiSummary({ mock: true })}
                        disabled={aiIsThinking}
                        title={t('oth360Results.summarySection.mockTitle')}
                      >
                        {t('oth360Results.summarySection.mockLocal')}
                      </button>
                    )}
                  </div>

                  {aiError && <p className={s.aiErrorText}>{aiError}</p>}
                </div>
              );
            })()}
          </>
        )}
      </main>
    </div>
  );
};
